import React, { useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button,
  ThemeProvider,
  createTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModalStackContext, useModalStack } from '../contexts/ModalStackContext';
import './AppModal.css';

/**
 * Standardized modal component with deterministic z-index stacking.
 * Nested AppModals automatically get higher z-index values.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to call when modal is closed
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.icon - Optional icon to display next to title
 * @param {React.ReactNode} props.children - Modal content
 * @param {React.ReactNode} props.actions - Modal action buttons
 * @param {string} props.maxWidth - Maximum width of modal ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {boolean} props.fullWidth - Whether modal should take full width
 * @param {boolean} props.showCloseButton - Whether to show X close button in header
 * @param {string} props.className - Additional CSS class name
 */
export const AppModal = ({
  open,
  onClose,
  title,
  icon,
  children,
  actions,
  maxWidth = 'md',
  fullWidth = true,
  showCloseButton = true,
  dividers = true,
  className = '',
  ...props
}) => {
  const { zIndexes, nextDepth } = useModalStack();

  const modalStyle = { zIndex: zIndexes.modal };

  const zIndexOverrides = useMemo(() => ({
    components: {
      MuiPopover: {
        styleOverrides: {
          root: {
            zIndex: zIndexes.popover,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: {
            zIndex: zIndexes.menu,
          },
        },
      },
    },
  }), [zIndexes.popover, zIndexes.menu]);

  const themeWithZIndex = useCallback(
    (parentTheme) => createTheme(parentTheme, zIndexOverrides),
    [zIndexOverrides]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      className={`app-modal ${className}`}
      style={modalStyle}
      slotProps={{
        backdrop: {
          className: 'app-modal-backdrop',
          style: { zIndex: zIndexes.backdrop },
        },
      }}
      PaperProps={{
        className: 'app-modal-paper',
        style: { zIndex: zIndexes.paper },
      }}
      {...props}
    >
      {title && (
        <DialogTitle className="app-modal-title">
          <Box className="app-modal-title-content">
            {icon && (
              <Box className="app-modal-title-icon">
                {icon}
              </Box>
            )}
            <Typography variant="h6" component="div" className="app-modal-title-text">
              {title}
            </Typography>
          </Box>
          {showCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              className="app-modal-close-button"
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent className="app-modal-content" dividers={dividers}>
        <ThemeProvider theme={themeWithZIndex}>
          <ModalStackContext.Provider value={nextDepth}>
            {children}
          </ModalStackContext.Provider>
        </ThemeProvider>
      </DialogContent>

      {(actions || showCloseButton) ? (
        <DialogActions className="app-modal-actions">
          {actions}
          {!actions && showCloseButton && (
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
          )}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AppModal;
